/**
 * Billing Utility Functions
 *
 * Formatting helpers and constants for Instructor Accounting
 * (supports both Quarterly and Monthly billing periods)
 */

import type {
  CourseAccountingDto,
  CourseSessionAccountingDto,
  CourseSessionDto,
} from '@/types/api.types'
import { BillingPeriodType, InstructorAccountStatus } from '@/types/api.types'

/**
 * Format amount in Euro: 1274 → "1.274,00 €"
 */
export function formatCurrency(amount: number): string {
  return amount.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  })
}

/**
 * Format hours: 12.5 → "12,5 Std."
 */
export function formatHours(hours: number): string {
  return `${hours.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Std.`
}

// ---------------------------------------------------------------------------
// Month names (German)
// ---------------------------------------------------------------------------

const MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
] as const

/**
 * Get German month name: 1→"Januar", 12→"Dezember"
 */
export function getMonthName(month: number): string {
  if (month < 1 || month > 12) return 'Unbekannt'
  return MONTH_NAMES[month - 1]!
}

// ---------------------------------------------------------------------------
// Period formatting (Quarterly + Monthly)
// ---------------------------------------------------------------------------

/**
 * Format period label depending on type:
 * - Quarterly: "Q1 2026"
 * - Monthly: "Januar 2026"
 */
export function formatPeriod(
  year: number,
  period: number,
  type: BillingPeriodType = BillingPeriodType.Quarterly
): string {
  if (type === BillingPeriodType.Monthly) {
    return `${getMonthName(period)} ${year}`
  }
  return `Q${period} ${year}`
}

/**
 * Determine current period from today's date.
 * - Quarterly: { year, period: 1-4 }
 * - Monthly: { year, period: 1-12 }
 */
export function getCurrentPeriod(type: BillingPeriodType = BillingPeriodType.Quarterly): {
  year: number
  period: number
} {
  const now = new Date()
  if (type === BillingPeriodType.Monthly) {
    return { year: now.getFullYear(), period: now.getMonth() + 1 }
  }
  const quarter = Math.ceil((now.getMonth() + 1) / 3)
  return { year: now.getFullYear(), period: quarter }
}

/**
 * Check if given year/period matches the current period for the given type.
 */
export function isCurrentPeriod(
  year: number,
  period: number,
  type: BillingPeriodType = BillingPeriodType.Quarterly
): boolean {
  const current = getCurrentPeriod(type)
  return current.year === year && current.period === period
}

/**
 * Map InstructorAccountStatus to Badge severity string
 */
export function getStatusSeverity(status: InstructorAccountStatus): string {
  switch (status) {
    case InstructorAccountStatus.Draft:
      return 'secondary'
    case InstructorAccountStatus.Submitted:
      return 'info'
    case InstructorAccountStatus.Authorized:
      return 'warn'
    case InstructorAccountStatus.Approved:
      return 'success'
    case InstructorAccountStatus.PaidOut:
      return 'contrast'
    default:
      return 'secondary'
  }
}

/**
 * Map InstructorAccountStatus to German display label
 */
export function getStatusLabel(status: InstructorAccountStatus): string {
  switch (status) {
    case InstructorAccountStatus.Draft:
      return 'Entwurf'
    case InstructorAccountStatus.Submitted:
      return 'Eingereicht'
    case InstructorAccountStatus.Authorized:
      return 'Genehmigt'
    case InstructorAccountStatus.Approved:
      return 'Freigegeben'
    case InstructorAccountStatus.PaidOut:
      return 'Ausgezahlt'
    default:
      return 'Unbekannt'
  }
}

// ---------------------------------------------------------------------------
// Monthly breakdown helpers (for Quarterly detail view)
// ---------------------------------------------------------------------------

export interface QuarterMonth {
  /** 0-based month index (0 = January) */
  month: number
  /** German month name */
  name: string
}

/**
 * Get the 3 months belonging to a quarter
 * Q1 → [Jan, Feb, Mär], Q2 → [Apr, Mai, Jun], etc.
 */
export function getQuarterMonths(quarter: number): QuarterMonth[] {
  const startMonth = (quarter - 1) * 3
  return [0, 1, 2].map((offset) => ({
    month: startMonth + offset,
    name: MONTH_NAMES[startMonth + offset]!,
  }))
}

/**
 * Format an ISO datetime string as "Di, 07.01."
 * Accepts full datetime (e.g. "2026-01-07T18:00:00") or date-only strings.
 */
export function formatSessionDate(isoDatetime: string): string {
  const date = new Date(isoDatetime)
  const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'] as const
  const day = dayNames[date.getDay()]!
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}, ${dd}.${mm}.`
}

/** Per-course data for a single month */
export interface MonthlyCourseData {
  courseId: number
  courseName: string
  courseNumber: number
  sessions: CourseSessionAccountingDto[]
  leadHours: number
  coHours: number
  totalHours: number
  amount: number
  leadRate: number
  coRate: number
}

/**
 * Group course accountings' sessions by month.
 * Returns a map: month-index → MonthlyCourseData[]
 */
export function groupByMonth(
  courses: CourseAccountingDto[],
  months: QuarterMonth[]
): Map<number, MonthlyCourseData[]> {
  const result = new Map<number, MonthlyCourseData[]>()
  for (const m of months) {
    result.set(m.month, [])
  }

  for (const course of courses) {
    if (!course.sessionAccountings) continue

    // Group sessions of this course by month
    const byMonth = new Map<number, CourseSessionAccountingDto[]>()
    for (const session of course.sessionAccountings) {
      const monthIdx = new Date(session.sessionDate).getMonth()
      if (!byMonth.has(monthIdx)) byMonth.set(monthIdx, [])
      byMonth.get(monthIdx)!.push(session)
    }

    // Build per-month course data
    for (const [monthIdx, sessions] of byMonth) {
      const leadSessions = sessions.filter((s) => s.isLeadInstructor)
      const coSessions = sessions.filter((s) => !s.isLeadInstructor)
      const leadHrs = leadSessions.reduce((sum, s) => sum + s.hours, 0)
      const coHrs = coSessions.reduce((sum, s) => sum + s.hours, 0)

      const monthCourses = result.get(monthIdx)
      if (monthCourses) {
        monthCourses.push({
          courseId: course.courseId,
          courseName: course.courseName ?? '',
          courseNumber: course.courseNumber ?? 0,
          sessions: sessions.sort((a, b) => a.sessionDate.localeCompare(b.sessionDate)),
          leadHours: leadHrs,
          coHours: coHrs,
          totalHours: leadHrs + coHrs,
          amount: sessions.reduce((sum, s) => sum + s.amount, 0),
          leadRate: course.leadInstructorRate,
          coRate: course.coInstructorRate,
        })
      }
    }
  }

  return result
}

// ---------------------------------------------------------------------------
// Period date helpers
// ---------------------------------------------------------------------------

/**
 * End date of a quarter: Q1→31.03., Q2→30.06., Q3→30.09., Q4→31.12.
 */
export function getQuarterEndDate(year: number, quarter: number): Date {
  // Month after quarter end (0-based): Q1→3, Q2→6, Q3→9, Q4→12→0(next year)
  const monthAfter = quarter * 3
  // Day 0 of month X = last day of month X-1
  return new Date(year, monthAfter, 0, 23, 59, 59, 999)
}

/**
 * Get date range for a period (start and end dates).
 * - Quarterly: Q1→Jan 1 – Mar 31, etc.
 * - Monthly: period 3→Mar 1 – Mar 31, etc.
 */
export function getPeriodDateRange(
  year: number,
  period: number,
  type: BillingPeriodType
): { start: Date; end: Date } {
  if (type === BillingPeriodType.Monthly) {
    const start = new Date(year, period - 1, 1)
    const end = new Date(year, period, 0, 23, 59, 59, 999) // last day of month
    return { start, end }
  }
  // Quarterly
  const start = new Date(year, (period - 1) * 3, 1)
  const end = getQuarterEndDate(year, period)
  return { start, end }
}

/**
 * End date of a period (Quarterly or Monthly).
 */
export function getPeriodEndDate(year: number, period: number, type: BillingPeriodType): Date {
  return getPeriodDateRange(year, period, type).end
}

/**
 * All creatable periods (current year up to current period + previous year).
 * Sorted descending (newest first).
 */
export function getCreatablePeriods(
  type: BillingPeriodType = BillingPeriodType.Quarterly
): { year: number; period: number }[] {
  const { year, period } = getCurrentPeriod(type)
  const result: { year: number; period: number }[] = []

  if (type === BillingPeriodType.Monthly) {
    // Current year: month 1 to current month
    for (let m = period; m >= 1; m--) {
      result.push({ year, period: m })
    }
    // Previous year: all 12 months
    for (let m = 12; m >= 1; m--) {
      result.push({ year: year - 1, period: m })
    }
  } else {
    // Current year: Q1 to current quarter
    for (let q = period; q >= 1; q--) {
      result.push({ year, period: q })
    }
    // Previous year: Q4 down to Q1
    for (let q = 4; q >= 1; q--) {
      result.push({ year: year - 1, period: q })
    }
  }

  return result
}

/**
 * Find the latest session date within a specific period across all courses.
 */
export function getLastSessionInPeriod(
  allSessions: Map<number, CourseSessionDto[]>,
  year: number,
  period: number,
  type: BillingPeriodType = BillingPeriodType.Quarterly
): Date | null {
  const { start: periodStart, end: periodEnd } = getPeriodDateRange(year, period, type)
  let latest: Date | null = null

  for (const sessions of allSessions.values()) {
    for (const s of sessions) {
      const d = new Date(s.date)
      if (d >= periodStart && d <= periodEnd) {
        if (!latest || d > latest) latest = d
      }
    }
  }

  return latest
}
